/**
 * Cart Quantity Limit Handler
 * Enforces best-by date quantity limits when updating quantities in cart drawer and cart page
 */

console.log('CartQuantityLimit: Script loaded');

if (!customElements.get('cart-quantity-limit')) {
  class CartQuantityLimit extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    connectedCallback() {
      this.init();
    }

    init() {
      // Find cart-items element
      const cartItems = document.querySelector('cart-items');
      if (!cartItems) {
        // Retry if cart-items isn't loaded yet
        setTimeout(() => this.init(), 100);
        return;
      }

      // Intercept the updateQuantity method
      this.interceptCartQuantityUpdates(cartItems);
      
      // Also intercept quantity input changes directly
      this.interceptQuantityInputs();
    }

    /**
     * Intercepts the cart-items updateQuantity method
     */
    interceptCartQuantityUpdates(cartItems) {
      const originalUpdateQuantity = cartItems.updateQuantity.bind(cartItems);
      
      cartItems.updateQuantity = async (line, quantity, name) => {
        // Get the line item element
        const lineItem = document.getElementById(`cart-item-${line}`);
        if (!lineItem) {
          return originalUpdateQuantity(line, quantity, name);
        }

        // Check if this item has best-by date properties
        const variantId = lineItem.dataset.variantId;
        const productId = lineItem.dataset.productId;
        
        // Get best-by date from line item properties (from the rendered HTML)
        const bestByDateValue = this.getBestByDateFromCartItem(lineItem);
        
        if (!bestByDateValue) {
          // No best-by date, allow normal update
          return originalUpdateQuantity(line, quantity, name);
        }

        // Validate quantity against limit
        const validation = await this.validateCartItemQuantity(productId, variantId, bestByDateValue, quantity, line, lineItem);
        
        if (!validation.valid) {
          console.log('CartQuantityLimit: Blocked quantity update:', validation.message);
          this.showError(lineItem, validation.message);
          
          // Restore original quantity
          const quantityInput = lineItem.querySelector('.qty-input__input');
          if (quantityInput) {
            quantityInput.value = quantityInput.dataset.initialValue || 1;
          }
          
          return; // Don't proceed with update
        }

        // Validation passed, proceed with update
        return originalUpdateQuantity(line, quantity, name);
      };
    }

    /**
     * Intercepts quantity input changes directly
     */
    interceptQuantityInputs() {
      // Use event delegation since cart items are dynamically rendered
      document.addEventListener('change', async (e) => {
        const input = e.target;
        if (!input.classList.contains('qty-input__input') || !input.dataset.index) {
          return;
        }

        const line = parseInt(input.dataset.index, 10);
        const quantity = parseInt(input.value, 10);
        const lineItem = document.getElementById(`cart-item-${line}`);
        
        if (!lineItem || isNaN(quantity) || quantity < 0) {
          return;
        }

        // Check if this item has best-by date properties
        const variantId = lineItem.dataset.variantId;
        const productId = lineItem.dataset.productId;
        const bestByDateValue = this.getBestByDateFromCartItem(lineItem);
        
        if (!bestByDateValue) {
          return; // No best-by date, allow normal update
        }

        // Validate quantity
        const validation = await this.validateCartItemQuantity(productId, variantId, bestByDateValue, quantity, line, lineItem);
        
        if (!validation.valid) {
          console.log('CartQuantityLimit: Blocked quantity input change:', validation.message);
          e.preventDefault();
          e.stopPropagation();
          
          // Restore original quantity
          input.value = input.dataset.initialValue || 1;
          this.showError(lineItem, validation.message);
          
          return false;
        }
      }, true); // Capture phase

      // Also intercept plus button clicks
      document.addEventListener('click', async (e) => {
        const button = e.target.closest('.qty-input__btn.btn--plus');
        if (!button) return;

        const quantityInput = button.closest('quantity-input')?.querySelector('.qty-input__input');
        if (!quantityInput || !quantityInput.dataset.index) return;

        const line = parseInt(quantityInput.dataset.index, 10);
        const lineItem = document.getElementById(`cart-item-${line}`);
        if (!lineItem) return;

        const variantId = lineItem.dataset.variantId;
        const productId = lineItem.dataset.productId;
        const bestByDateValue = this.getBestByDateFromCartItem(lineItem);
        
        if (!bestByDateValue) return; // No best-by date, allow normal update

        const currentQty = parseInt(quantityInput.value, 10) || 0;
        const newQty = currentQty + 1;

        // Validate before allowing the increase
        const validation = await this.validateCartItemQuantity(productId, variantId, bestByDateValue, newQty, line, lineItem);
        
        if (!validation.valid) {
          console.log('CartQuantityLimit: Blocked plus button click:', validation.message);
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.showError(lineItem, validation.message);
          return false;
        }
      }, true); // Capture phase
    }

    /**
     * Gets the best-by date value from a cart item element
     */
    getBestByDateFromCartItem(lineItem) {
      // First try to get from data attribute (most reliable)
      const bestByDateAttr = lineItem.dataset.bestByDateValue;
      if (bestByDateAttr) {
        return bestByDateAttr;
      }

      // Fallback: Try to find the best-by date in the rendered HTML
      // It should be in the cart-item__variants section
      const variantsSection = lineItem.querySelector('.cart-item__variants');
      if (!variantsSection) return null;

      // Look for "Best By Date:" text
      const text = variantsSection.textContent || '';
      const match = text.match(/Best By Date:\s*([A-Za-z]+\s+\d{2},\s+\d{4})/);
      if (match) {
        // Convert formatted date back to YYYY-MM-DD
        const dateStr = match[1];
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }

      return null;
    }

    /**
     * Validates if a cart item quantity update would exceed the best-by date limit
     */
    async validateCartItemQuantity(productId, variantId, bestByDateValue, newQuantity, lineIndex, lineItem = null) {
      try {
        // Allow quantity decreases (removing items) - no limit check needed
        if (newQuantity <= 0) {
          return { valid: true, message: '' };
        }

        // Get the max quantity from data attribute (set by Liquid template)
        // Get lineItem if not provided
        if (!lineItem && lineIndex) {
          lineItem = document.getElementById(`cart-item-${lineIndex}`);
        }
        
        let maxQuantity = null;
        if (lineItem) {
          const maxQuantityAttr = lineItem.dataset.maxQuantity;
          if (maxQuantityAttr) {
            maxQuantity = parseInt(maxQuantityAttr, 10);
            if (isNaN(maxQuantity) || maxQuantity <= 0) {
              maxQuantity = null;
            }
          }
        }
        
        // If no max quantity set, allow update
        if (maxQuantity === null) {
          console.log('CartQuantityLimit: No max quantity set, allowing update');
          return { valid: true, message: '' };
        }

        // Fetch current cart to count existing quantities
        const cartUrl = (window.theme?.routes?.cart || '/cart') + '.js';
        const cartResponse = await fetch(cartUrl);
        if (!cartResponse.ok) {
          console.warn('CartQuantityLimit: Could not fetch cart for validation');
          return { valid: true, message: '' };
        }

        const cartData = await cartResponse.json();
        
        // Count how many items with this best-by date are in the cart (excluding current line)
        let cartQuantity = 0;
        let currentItemQuantity = 0;
        
        if (cartData.items && Array.isArray(cartData.items)) {
          cartData.items.forEach((item) => {
            // Check if this is the current item being updated
            const isCurrentItem = item.variant_id && String(item.variant_id) === String(variantId);
            
            if (isCurrentItem && item.properties) {
              let itemDateValue = null;
              
              // Handle properties as array or object
              if (Array.isArray(item.properties)) {
                const bestByDateProp = item.properties.find(prop => 
                  prop && (prop.name === '_best_by_date_value' || prop.name === 'Best By Date')
                );
                if (bestByDateProp) {
                  itemDateValue = bestByDateProp.value;
                }
              } else if (typeof item.properties === 'object') {
                if (item.properties['_best_by_date_value']) {
                  itemDateValue = item.properties['_best_by_date_value'];
                } else if (item.properties['Best By Date']) {
                  itemDateValue = item.properties['Best By Date'];
                }
              }
              
              // Check if same best-by date
              if (itemDateValue === bestByDateValue) {
                // This is the current item - store its current quantity
                currentItemQuantity = item.quantity || 0;
                // Don't add it to cartQuantity (we'll use newQuantity instead)
                return;
              }
            }
            
            // Check if same variant and best-by date (but not the current item)
            if (item.variant_id && String(item.variant_id) === String(variantId)) {
              if (item.properties) {
                let itemDateValue = null;
                
                // Handle properties as array or object
                if (Array.isArray(item.properties)) {
                  const bestByDateProp = item.properties.find(prop => 
                    prop && (prop.name === '_best_by_date_value' || prop.name === 'Best By Date')
                  );
                  if (bestByDateProp) {
                    itemDateValue = bestByDateProp.value;
                  }
                } else if (typeof item.properties === 'object') {
                  if (item.properties['_best_by_date_value']) {
                    itemDateValue = item.properties['_best_by_date_value'];
                  } else if (item.properties['Best By Date']) {
                    itemDateValue = item.properties['Best By Date'];
                  }
                }
                
                if (itemDateValue === bestByDateValue) {
                  cartQuantity += item.quantity || 0;
                }
              }
            }
          });
        }

        // Calculate total quantity: other items in cart + new quantity for this item
        const totalQuantity = cartQuantity + newQuantity;

        console.log('CartQuantityLimit: Validation check:', {
          productId: productId,
          variantId: variantId,
          bestByDateValue: bestByDateValue,
          newQuantity: newQuantity,
          currentItemQuantity: currentItemQuantity,
          cartQuantity: cartQuantity,
          totalQuantity: totalQuantity,
          maxQuantity: maxQuantity,
          lineItem: lineItem,
          maxQuantityAttr: lineItem?.dataset.maxQuantity
        });

        if (totalQuantity > maxQuantity) {
          const available = maxQuantity - cartQuantity;
          if (available <= 0) {
            return {
              valid: false,
              message: `You already have ${cartQuantity} ${cartQuantity === 1 ? 'item' : 'items'} with this best-by date in your cart. The maximum is ${maxQuantity}.`
            };
          } else {
            return {
              valid: false,
              message: `You can only add ${available} more ${available === 1 ? 'item' : 'items'} with this best-by date. You already have ${cartQuantity} in your cart.`
            };
          }
        }

        return { valid: true, message: '' };
      } catch (error) {
        console.error('CartQuantityLimit: Error validating cart item quantity:', error);
        // On error, allow update (don't block if we can't check)
        return { valid: true, message: '' };
      }
    }

    /**
     * Gets the max quantity for a best-by date from product metafields
     * This requires fetching the product data
     */
    async getMaxQuantityForBestByDate(productId, bestByDateValue) {
      try {
        // Fetch product data to get metafields
        // Note: This requires the product to be accessible via API
        // Alternative: Store the max quantity in a data attribute when rendering cart items
        const productUrl = `/products/${productId}.js`;
        const productResponse = await fetch(productUrl);
        
        if (!productResponse.ok) {
          console.warn('CartQuantityLimit: Could not fetch product data');
          return null;
        }

        const productData = await productResponse.json();
        
        // Get all best-by dates and quantities from metafields
        const primaryDate = productData.metafields?.custom?.best_by_date?.value;
        const secondaryDate = productData.metafields?.custom?.secondary_best_by_date?.value;
        const tertiaryDate = productData.metafields?.custom?.tertiary_best_by_date?.value;
        
        const primaryQty = productData.metafields?.custom?.primary_quantity?.value || 0;
        const secondaryQty = productData.metafields?.custom?.secondary_quantity?.value || 0;
        const tertiaryQty = productData.metafields?.custom?.tertiary_quantity?.value || 0;

        // Match the bestByDateValue to the appropriate quantity
        if (primaryDate && this.datesMatch(primaryDate, bestByDateValue)) {
          return parseInt(primaryQty, 10);
        }
        if (secondaryDate && this.datesMatch(secondaryDate, bestByDateValue)) {
          return parseInt(secondaryQty, 10);
        }
        if (tertiaryDate && this.datesMatch(tertiaryDate, bestByDateValue)) {
          return parseInt(tertiaryQty, 10);
        }

        return null;
      } catch (error) {
        console.error('CartQuantityLimit: Error fetching product metafields:', error);
        return null;
      }
    }

    /**
     * Checks if two date strings match (handles different formats)
     */
    datesMatch(date1, date2) {
      // Normalize both dates to YYYY-MM-DD format
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      
      if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
        return date1 === date2; // Fallback to string comparison
      }

      return d1.getFullYear() === d2.getFullYear() &&
             d1.getMonth() === d2.getMonth() &&
             d1.getDate() === d2.getDate();
    }

    /**
     * Shows an error message for a cart item
     */
    showError(lineItem, message) {
      // Remove existing error
      const existingError = lineItem.querySelector('.cart-item__error--quantity-limit');
      if (existingError) {
        existingError.remove();
      }

      // Create error element
      const errorDiv = document.createElement('div');
      errorDiv.className = 'cart-item__error cart-item__error--quantity-limit alert mt-2 bg-error-bg text-error-text text-sm';
      errorDiv.textContent = message;
      
      // Insert after the quantity input
      const quantityInput = lineItem.querySelector('quantity-input');
      if (quantityInput) {
        quantityInput.parentNode.insertBefore(errorDiv, quantityInput.nextSibling);
      } else {
        // Fallback: insert at end of cart item
        lineItem.appendChild(errorDiv);
      }

      // Remove error after 5 seconds
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.remove();
        }
      }, 5000);
    }
  }

  customElements.define('cart-quantity-limit', CartQuantityLimit);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const cartQuantityLimit = document.createElement('cart-quantity-limit');
    document.body.appendChild(cartQuantityLimit);
  });
} else {
  const cartQuantityLimit = document.createElement('cart-quantity-limit');
  document.body.appendChild(cartQuantityLimit);
}

